import React, { useState } from "react";
import styles from "./RechargeModal.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from "../../../../services/api";

// Presets alineados con la app móvil
const PRESET_AMOUNTS = [
  { label: "10k", value: 10000 },
  { label: "20k", value: 20000 },
  { label: "50k", value: 50000 },
  { label: "100k", value: 100000 },
  { label: "200k", value: 200000 },
  { label: "500k", value: 500000 },
];

const MIN_AMOUNT = 5000;
const MAX_AMOUNT = 9999999;

/**
 * Modal para elegir monto de recarga.
 * onRecharge(amount) debe resolver o lanzar / rechazar con Error(message).
 */
export default function RechargeModal({ isOpen, onClose, onRecharge, currentBalance }) {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  React.useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedAmount(null);
      setCustomAmount("");
      setError("");
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePresetClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount(amount.toString());
    setError("");
  };

  const handleCustomChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomAmount(value);
    setSelectedAmount(null);
    setError("");
  };

  const getFinalAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseInt(customAmount, 10);
    return 0;
  };

  const validateAmount = () => {
    const amount = getFinalAmount();
    if (!amount || amount < MIN_AMOUNT) {
      setError(`El monto mínimo es ${formatCurrency(MIN_AMOUNT)}`);
      return false;
    }
    if (amount > MAX_AMOUNT) {
      setError(`El monto máximo es ${formatCurrency(MAX_AMOUNT)}`);
      return false;
    }
    return true;
  };

  const handleDeposit = async () => {
    if (!validateAmount()) return;

    setLoading(true);
    setError("");
    try {
      const amount = getFinalAmount();
      await onRecharge(amount);
      onClose();
    } catch (err) {
      setError(
        err?.message || "Error al procesar la recarga. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <span className={styles.dollarIcon}>$</span>
          </div>
          <h2 className={styles.modalTitle}>Recargar Cuenta</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.subtitle}>
            Elige un monto o ingresa uno manualmente.
          </p>
          {currentBalance != null ? (
            <p className={styles.hint}>
              Saldo actual: {formatCurrency(currentBalance)}
            </p>
          ) : null}

          <div className={styles.presetsGrid}>
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={`${styles.presetBtn} ${
                  selectedAmount === preset.value ? styles.presetBtnActive : ""
                }`}
                onClick={() => handlePresetClick(preset.value)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className={styles.customAmountWrapper}>
            <span className={styles.currencySymbol}>
              <FontAwesomeIcon icon={faDollarSign} />
            </span>
            <input
              type="text"
              className={styles.customInput}
              placeholder="Otro Monto"
              value={
                customAmount
                  ? parseInt(customAmount, 10).toLocaleString("es-CO")
                  : ""
              }
              onChange={handleCustomChange}
              inputMode="numeric"
            />
          </div>

          <p className={styles.limits}>
            Min: {formatCurrency(MIN_AMOUNT)} Max:{" "}
            {formatCurrency(MAX_AMOUNT)}
          </p>

          {error && <p className={styles.errorText}>{error}</p>}

          <button
            type="button"
            className={styles.depositBtn}
            onClick={handleDeposit}
            disabled={loading || (!selectedAmount && !customAmount)}
          >
            {loading
              ? "Procesando..."
              : `Depositar${
                  getFinalAmount()
                    ? `: ${getFinalAmount().toLocaleString("es-CO")}`
                    : ""
                }`}
          </button>
        </div>
      </div>
    </div>
  );
}
